/**
 * Sovereign AI Client for Elite Business Group
 * Connects directly to the Soup-powered local LLM cluster running on Dell Ground Server
 * Routed via Private WireGuard/Tailscale Mesh or Edge Caddy Gateway (ai.elitestc.com).
 *
 * Supported Models:
 * - soup-trading-erp:latest (Fine-tuned Qwen2.5 domain model for Elite Success Trading)
 * - qwen2.5:3b (General reasoning and multi-turn workflow assistant)
 * - nomic-embed-text:latest (High-density semantic embeddings for RAG)
 */

export interface ChatMessage {
  role: "system" | "user" | "assistant";
  content: string;
}

export interface SoupAiOptions {
  model?: string;
  baseUrl?: string;
  apiKey?: string;
  temperature?: number;
  maxTokens?: number;
  timeoutMs?: number;
}

export const DEFAULT_SOUP_CONFIG = {
  // Use private mesh tunnel in production/backend, or public Caddy domain
  endpoint:
    (typeof process !== "undefined" && process.env?.VITE_SOUP_AI_ENDPOINT) ||
    (typeof process !== "undefined" && process.env?.SOUP_AI_ENDPOINT) ||
    "http://100.105.50.23:11434/v1",
  defaultModel: "soup-trading-erp:latest",
  fallbackModel: "qwen2.5:3b",
  embeddingModel: "nomic-embed-text:latest",
};

export class SoupAiClient {
  private baseUrl: string;
  private apiKey: string;
  private defaultModel: string;

  constructor(options?: SoupAiOptions) {
    this.baseUrl = (options?.baseUrl || DEFAULT_SOUP_CONFIG.endpoint).replace(/\/$/, "");
    this.apiKey = options?.apiKey || "sovereign-soup-key";
    this.defaultModel = options?.model || DEFAULT_SOUP_CONFIG.defaultModel;
  }

  /**
   * Health check and active model discovery.
   */
  async listModels(): Promise<string[]> {
    try {
      const res = await fetch(`${this.baseUrl}/models`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${this.apiKey}`,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      const data = await res.json();
      return (data.data || []).map((m: any) => m.id);
    } catch (err: any) {
      console.error("[SoupAiClient] Failed to list models:", err.message);
      return [];
    }
  }

  /**
   * Send a chat completion request compliant with OpenAI /v1/chat/completions.
   */
  async chatCompletion(
    messages: ChatMessage[],
    options?: Partial<SoupAiOptions>
  ): Promise<string> {
    const model = options?.model || this.defaultModel;
    const maxTokens = options?.maxTokens || 512;
    const temperature = options?.temperature ?? 0.3;

    const res = await fetch(`${this.baseUrl}/chat/completions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model,
        messages,
        max_tokens: maxTokens,
        temperature,
        stream: false,
      }),
    });

    if (!res.ok) {
      const errorBody = await res.text().catch(() => "");
      throw new Error(`Soup AI error (${res.status}): ${errorBody || res.statusText}`);
    }

    const data = await res.json();
    return data.choices?.[0]?.message?.content || "";
  }

  /**
   * Domain-specific task: Draft commercial correspondence according to KSA regulations.
   */
  async draftCommercialProposal(
    clientName: string,
    scopeOfWork: string,
    totalSar: number
  ): Promise<string> {
    const systemPrompt: ChatMessage = {
      role: "system",
      content:
        "You are the senior commercial consultant at Elite Success Trading & Contracting Est. in Riyadh, Saudi Arabia. " +
        "You draft professional, compliant, and legally structured commercial proposals in bilingual Arabic/English format.",
    };

    const userPrompt: ChatMessage = {
      role: "user",
      content: `Draft a commercial proposal letter for client "${clientName}". Scope of work: "${scopeOfWork}". Total value: SAR ${totalSar.toLocaleString()} plus 15% VAT. Standard terms: 50% advance, 50% upon delivery/completion.`,
    };

    return this.chatCompletion([systemPrompt, userPrompt], { maxTokens: 800 });
  }

  /**
   * Domain-specific task: Analyze an invoice text or draft for ZATCA Phase-2 audit compliance.
   */
  async auditZatcaInvoiceCompliance(invoiceDetails: Record<string, any>): Promise<string> {
    const systemPrompt: ChatMessage = {
      role: "system",
      content:
        "You are the chief tax & financial systems auditor for Elite Success Trading Est. " +
        "You review invoice fields against Saudi ZATCA Phase-2 e-invoicing compliance requirements.",
    };

    const userPrompt: ChatMessage = {
      role: "user",
      content: `Review the following invoice data and confirm if required fields (Buyer VAT, Seller VAT, UUID, Line Item VAT, Total SAR) are complete:\n${JSON.stringify(
        invoiceDetails,
        null,
        2
      )}`,
    };

    return this.chatCompletion([systemPrompt, userPrompt], { maxTokens: 600 });
  }
}

export const soupAi = new SoupAiClient();

export async function querySoupModel(
  messages: ChatMessage[],
  options?: SoupAiOptions
): Promise<string> {
  const client = new SoupAiClient(options);
  return client.chatCompletion(messages, options);
}

